import {
	DIRECT_MESSAGE_RECEIVED,
	NOTIFICATION_INVITE_ANSWERED,
	NOTIFICATION_PAGE_APPENDED,
	NOTIFICATION_PAGE_LOADING,
	NOTIFICATION_PUSHED,
	NOTIFICATION_TOAST_DEQUEUED,
	NOTIFICATION_TOAST_ENQUEUED,
	NOTIFICATIONS_LOADED,
	NOTIFICATIONS_MARKED_READ,
	NOTIFICATIONS_REMOVED,
} from 'react/redux/actions/notifications';
import { DIRECT_MESSAGE_READ } from 'react/redux/actions/directMessageRead';

export const initialNotificationsState = {
	loaded: false,
	items: [],
	unreadCount: 0,
	conversations: [],
	unreadConversationCount: 0,
	hasMore: false,
	loadingMore: false,
	toastQueue: [],
	// Toasts already shown on this page, so a reload of the section doesn't
	// queue them again before the server records them.
	shownToastIds: [],
};

// Newest first, one entry per id; later lists win for ids they share.
function mergeItems(newer, older) {
	const byId = new Map();
	older.forEach((item) => byId.set(item.id, item));
	newer.forEach((item) => byId.set(item.id, item));
	return Array.from(byId.values()).sort((first, second) => second.id - first.id);
}

function enqueueToast(state, item) {
	if(!item || state.shownToastIds.includes(item.id) || state.toastQueue.some((queued) => queued.id === item.id)) {
		return state;
	}
	return { ...state, toastQueue: [...state.toastQueue, item] };
}

// The bootstrap section, or the section wrapped under its own name.
function unwrapSection(section) {
	return section && section.notifications && !Array.isArray(section.notifications) ? section.notifications : (section || {});
}

function loadSection(state, rawSection) {
	const section = unwrapSection(rawSection);
	const sectionItems = section.items || [];
	const keepsOlderPages = state.items.length > sectionItems.length;
	let nextState = {
		...state,
		loaded: true,
		items: keepsOlderPages ? mergeItems(sectionItems, state.items) : mergeItems(sectionItems, []),
		unreadCount: Number(section.unread_count) || 0,
		conversations: section.conversations || [],
		unreadConversationCount: Number(section.unread_conversation_count) || 0,
		hasMore: keepsOlderPages ? state.hasMore : !!section.has_more,
	};
	(section.toasts || []).forEach((toast) => {
		nextState = enqueueToast(nextState, toast);
	});
	return nextState;
}

// The Messages group holds as many conversations as the server sends
// (services\notifications\UnreadConversations::LIMIT).
const CONVERSATION_LIMIT = 5;

function receiveDirectMessage(state, { sender, preview, sentAt }) {
	const existing = state.conversations.find((conversation) => conversation.player.id === sender.id);
	const conversation = {
		player: existing ? existing.player : sender,
		unread_count: existing ? existing.unread_count + 1 : 1,
		preview,
		sent_at: sentAt,
	};
	const others = state.conversations.filter((candidate) => candidate !== existing);
	return {
		...state,
		conversations: [conversation, ...others].slice(0, CONVERSATION_LIMIT),
		unreadConversationCount: existing ? state.unreadConversationCount : state.unreadConversationCount + 1,
	};
}

function markRead(state, { ids, all, unreadCount }) {
	let newlyRead = 0;
	const items = state.items.map((item) => {
		if(item.read || !(all || ids.includes(item.id))) {
			return item;
		}
		newlyRead++;
		return { ...item, read: true };
	});
	let nextUnreadCount = Math.max(0, state.unreadCount - newlyRead);
	if(all) {
		nextUnreadCount = 0;
	}
	if(unreadCount !== null && unreadCount !== undefined) {
		nextUnreadCount = unreadCount;
	}
	return { ...state, items, unreadCount: nextUnreadCount };
}

export default (state = initialNotificationsState, action) => {
	switch(action.type) {
		case NOTIFICATIONS_LOADED:
			return loadSection(state, action.payload);
		case NOTIFICATION_PUSHED: {
			const { item, unreadCount } = action.payload;
			return {
				...state,
				items: mergeItems([item], state.items),
				unreadCount: Number.isFinite(Number(unreadCount)) ? Number(unreadCount) : state.unreadCount,
			};
		}
		case NOTIFICATIONS_MARKED_READ:
			return markRead(state, action.payload);
		case NOTIFICATIONS_REMOVED: {
			const { ids, unreadCount } = action.payload;
			return {
				...state,
				items: state.items.filter((item) => !ids.includes(item.id)),
				unreadCount: Number.isFinite(Number(unreadCount)) && unreadCount !== null ? Number(unreadCount) : state.unreadCount,
			};
		}
		case NOTIFICATION_PAGE_APPENDED:
			return {
				...state,
				items: mergeItems(state.items, action.payload.items),
				hasMore: action.payload.hasMore,
			};
		case NOTIFICATION_PAGE_LOADING:
			return { ...state, loadingMore: action.payload };
		case NOTIFICATION_TOAST_ENQUEUED:
			return enqueueToast(state, action.payload);
		case NOTIFICATION_TOAST_DEQUEUED:
			return {
				...state,
				toastQueue: state.toastQueue.filter((item) => item.id !== action.payload),
				shownToastIds: state.shownToastIds.includes(action.payload) ? state.shownToastIds : [...state.shownToastIds, action.payload],
			};
		case DIRECT_MESSAGE_RECEIVED:
			return receiveDirectMessage(state, action.payload);
		case DIRECT_MESSAGE_READ:
			return {
				...state,
				conversations: state.conversations.filter((conversation) => conversation.player.id !== action.payload.otherPlayerId),
				unreadConversationCount: action.payload.unreadConversationCount,
			};
		case NOTIFICATION_INVITE_ANSWERED:
			return {
				...state,
				items: state.items.map((item) => (item.id === action.payload ? { ...item, data: { ...item.data, pending: false } } : item)),
			};
		default:
			return state;
	}
};
